import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Space, Breadcrumb, Button, Card, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PageHeader } from "@ant-design/pro-layout";

import type {
  ListApplicationsResponse,
  ApplicationListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import { ListApplicationsRequest } from "@chirpstack/chirpstack-api-grpc-web/api/application_pb";
import type { ListDevicesResponse } from "@chirpstack/chirpstack-api-grpc-web/api/device_pb";
import { ListDevicesRequest } from "@chirpstack/chirpstack-api-grpc-web/api/device_pb";
import type { Tenant } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";

import type { GetPageCallbackFunc } from "../../components/ResponsiveDataTable";
import ResponsiveDataTable from "../../components/ResponsiveDataTable";
import ApplicationStore from "../../stores/ApplicationStore";
import DeviceStore from "../../stores/DeviceStore";
import Admin from "../../components/Admin";
import { useTitle } from "../helpers";

interface IProps {
  tenant: Tenant;
}

function ListApplications(props: IProps) {
  const [deviceCounts, setDeviceCounts] = useState<Record<string, number>>({});
  useTitle("Tenants", props.tenant.getName(), "Applications");
  const columns: ColumnsType<ApplicationListItem.AsObject> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (text, record) => <Link to={`/tenants/${props.tenant.getId()}/applications/${record.id}`}>{text}</Link>,
    },
    {
      title: "Registered Devices",
      dataIndex: "deviceCount",
      key: "deviceCount",
      width: 150,
      render: (text, record) => {
        const count = deviceCounts[record.id];
        return count !== undefined ? count : '...';
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
  ];

  const getPage = (
    limit: number,
    offset: number,
    _filters: object,
    orderBy: string | void,
    orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => {
    const req = new ListApplicationsRequest();
    req.setTenantId(props.tenant.getId());
    req.setLimit(limit);
    req.setOffset(offset);

    ApplicationStore.list(req, (resp: ListApplicationsResponse) => {
      const obj = resp.toObject();
      // Fetch device counts for all applications
      fetchAllDeviceCounts(obj.resultList);
      callbackFunc(obj.totalCount, obj.resultList);
    });
  };

  const fetchDeviceCount = (applicationId: string) => {
    const req = new ListDevicesRequest();
    req.setApplicationId(applicationId);
    req.setLimit(1); // We only need the count, not the actual devices
    req.setOffset(0);

    DeviceStore.list(req, (resp: ListDevicesResponse) => {
      setDeviceCounts(prev => ({
        ...prev,
        [applicationId]: resp.getTotalCount()
      }));
    });
  };

  const fetchAllDeviceCounts = (applications: ApplicationListItem.AsObject[]) => {
    applications.forEach(app => {
      if (deviceCounts[app.id] === undefined) {
        fetchDeviceCount(app.id);
      }
    });
  };

  const renderApplicationCard = (record: ApplicationListItem.AsObject) => {
    // Fetch device count if not already fetched
    if (deviceCounts[record.id] === undefined) {
      fetchDeviceCount(record.id);
    }

    return (
      <Card 
        key={record.id} 
        size="small" 
        style={{ marginBottom: 8 }}
        bodyStyle={{ padding: 12 }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {/* Header: Application Name */}
          <div>
            <Link to={`/tenants/${props.tenant.getId()}/applications/${record.id}`}>
              <Typography.Title level={5} style={{ margin: 0, fontSize: 16 }}>
                {record.name}
              </Typography.Title>
            </Link>
          </div>

          {/* Subheader: Device Count */}
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              Registered Devices: {deviceCounts[record.id] !== undefined ? deviceCounts[record.id] : '...'}
            </Typography.Text>
          </div>

          {record.description && (
            <div>
              <Typography.Text strong style={{ fontSize: 12, color: '#666' }}>
                Description:
              </Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: 14 }}>
                {record.description}
              </Typography.Text>
            </div>
          )}
        </Space>
      </Card>
    );
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb>
            <Breadcrumb.Item>
              <span>Tenants</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>
                <Link to={`/tenants/${props.tenant.getId()}`}>{props.tenant.getName()}</Link>
              </span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Applications</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
        title="Applications"
        extra={[
          <Admin tenantId={props.tenant.getId()} isDeviceAdmin>
            <Button type="primary">
              <Link to={`/tenants/${props.tenant.getId()}/applications/create`}>Add application</Link>
            </Button>
          </Admin>,
        ]}
      />
      <ResponsiveDataTable 
        columns={columns} 
        getPage={getPage} 
        rowKey="id"
        renderCard={renderApplicationCard}
      />
    </Space>
  );
}

export default ListApplications;
